import React, { useContext, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography, Grid, Tooltip, CircularProgress } from "@mui/material";
import { FormattedMessage } from "react-intl";
import APIContext from "../../../services/api/api";
import { URLS } from "../../../configuration/urls";
import { formatNumberShort } from "../../../components/formatter";

HighchartsMore(Highcharts);


export function AccumulativeCustomsCleared({ id }) {
  const { getData } = useContext(APIContext);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const result = await getData(
          `h7/kpi/mawb/${id}/parcels-cleared`,
          {},
          URLS.EC_CLEAR_CUSTOMS_BE()
        );
        if (Array.isArray(result)) setData(result || []);
      } catch (err) {
        console.error("Error fetching accumulative customs:", err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [id]);

  const createGaugeOptions = (clearedRate) => ({
    chart: {
      type: "gauge",
      height: 100,
      width: 200,
      backgroundColor: "transparent",
      margin: [0, 0, 0, 0],
      spacing: [0, 0, 0, 0]
    },
    title: { text: null },
    pane: {
      center: ["50%", "75%"],
      size: "120%",
      startAngle: -90,
      endAngle: 90,
      background: [
        {
          backgroundColor: "#99bdf3",
          innerRadius: "75%",
          outerRadius: "100%",
          shape: "arc",
          borderWidth: 0
        }
      ]
    },
    yAxis: {
      min: 0,
      max: 100,
      lineWidth: 0,
      tickWidth: 0,
      minorTickWidth: 0,
      tickAmount: 0,
      minorTickInterval: null,
      labels: { enabled: false },
      plotBands: [
        {
          from: 0,
          to: clearedRate || 0,
          color: "#005ae0",
          innerRadius: "75%",
          outerRadius: "100%"
        }
      ]
    },
    series: [
      {
        name: "Rate",
        data: [clearedRate || 0],
        dataLabels: { enabled: false },
        dial: {
          radius: "60%",
          backgroundColor: "var(--font-strong-default)",
          baseWidth: 2,
          baseLength: "0%",
          rearLength: "0%"
        },
        pivot: {
          radius: 3,
          backgroundColor: "#1a1a2e"
        },
        enableMouseTracking: false
      }
    ],
    tooltip: { enabled: false },
    credits: { enabled: false },
    legend: { enabled: false },
    exporting: { enabled: false }
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ backgroundColor: "var(--container-light)" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            borderBottom: "2px solid #e8ebef",
            py: 1,
            px: 2
          }}
        >
          <Typography
            color="var(--text-primary)"
            variant="subtitle1"
            fontWeight={600}
            data-testid={`accumulative_customs_cleared_box_heading`}
            fontSize="20px"
          >
            <FormattedMessage
              id="accumulativeCustomsCleared"
              defaultMessage="Accumulative Customs Cleared"
            />
          </Typography>
        </Box>

        {!Array.isArray(data) || data.length === 0 ? (
          <Typography
            style={{ textAlign: "center", padding: "3rem 0" }}
            variant="subtitle1"
          >
            <FormattedMessage id="noData" />
          </Typography>
        ) : (
          <Grid
            container
            spacing={2}
            justifyContent="space-around"
            alignItems="center"
            sx={{ px: 2, py: 3 }}
          >
            {data?.map?.((item, idx) => {
              const clearedRate = item?.parcelCleared || 0;
              const notClearedRate = item?.parcelNotCleared || 0;
              const totalParcels = item?.parcelClearedCount || 0;
              return (
                <Grid
                  item
                  key={idx}
                  xs={12}
                  sm={6}
                  md={4}
                  lg={(((data?.length || 0)) / 10) + 1}
                  xl={(((data?.length || 0)) / 10) + 1}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={createGaugeOptions(clearedRate)}
                    />
                    <Typography component="span" sx={{
                      position: "absolute",
                      top: "52%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "51px",
                      height: "24px",
                      fontFamily: "Inter",
                      fontSize: "16px",
                      fontWeight: 600,
                      lineHeight: 1.5,
                      letterSpacing: "normal",
                      textAlign: "center",
                      color: "var(--info-innovation-100)",
                      pointerEvents: "none",
                      whiteSpace: "nowrap",
                      display: "block"
                    }}>
                      {(clearedRate || 0).toFixed(1)}%
                    </Typography>
                  </Box>

                  {/* Legend */}
                  <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "10px", mt: "-4px", mb: "10px" }}>
                    {[
                      { color: "#005ae0", label: "Cleared" },
                      { color: "#99bdf3", label: "Not Cleared" }
                    ].map(({ color, label }) => (
                      <Box key={label} sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "4px" }}>
                        <Box sx={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: "Inter", fontSize: "9px", fontWeight: 600, color: "var(--font-medium-default)", lineHeight: 1 }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ width: "150px", textAlign: "center", fontFamily: "Inter", color: "var(--font-strong-default)" }}>
                    <Tooltip placement="right" title={totalParcels?.toLocaleString?.("de-DE")}>
                      <Typography sx={{ fontFamily: "Inter", fontSize: "20px", fontWeight: 600, lineHeight: 1.4, letterSpacing: "normal", color: "var(--font-strong-default)" }}>
                        {formatNumberShort(totalParcels)} Parcels
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontFamily: "Inter", fontSize: "16px", fontWeight: 600, lineHeight: 1.5, letterSpacing: "normal", color: "var(--font-strong-default)" }}>
                      Cleared
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </>
  );
}
